/* eslint-disable @typescript-eslint/no-explicit-any */
// executiveState — the ONE selector for "primary support" and "watch point" (owner 2026-09-26). The asset header,
// the Executive Summary and the Orchestrator brief read the same canonical state, so no surface can name a
// different contributor or watch (SR-361 watch predicate; SR-364 one canonical state, two narrative layers).

export interface BridgePillar { id: string; label: string; score: number; contribution_pp: number; status: string; evidence_class?: string }

export type WatchKind = 'pillar' | 'risk' | 'coverage' | 'none';
export interface ExecutiveState {
  support: { id: string; label: string; score: number; contribution: number; tier: string } | null;
  watch: { kind: WatchKind; id?: string; title: string; value: string; unit: string };
  riskState: string | null;
  elevatedCoreDrivers: number;
  coreDrivers: number;
  coverage: number | null;
  evidenceQuality: number | null;
  runTier: string | null;
  evidenceState: 'COMPLETE' | 'PARTIAL' | 'LIMITED';
  tail: string | null;
  // Owner ATOM review 2026-09-29: a withheld composite names no support / drag / strength.
  compositeWithheld: boolean;
}

export const WITHHELD_PILLAR_STRENGTH = 'No authoritative pillar-strength conclusion is issued because the composite is withheld.';

const ADVERSE_RISK = /elevated|high|critical/i;

export function executiveState(metrics: any): ExecutiveState {
  const bridge = metrics?.composite_bridge ?? null;
  const pillars: BridgePillar[] = (bridge?.pillars ?? []).filter((p: any) => p && p.status !== 'WITHHELD' && typeof p.score === 'number');
  const compositeWithheld = !!bridge?.composite_withheld;
  // Primary support: the bridge's sealed strongest evidence-eligible contributor (never an ESG E-only pillar).
  let sup = compositeWithheld ? null : pillars.find((p) => p.id === bridge?.strongest_measured_id) ?? null;
  if (!sup && pillars.length && !compositeWithheld) sup = pillars.filter((p) => p.id !== 'esg').reduce((a, b) => (b.contribution_pp > a.contribution_pp ? b : a), pillars[0]);
  // SR-368: the displayed tier is the pillar's EVIDENCE CLASS (e.g. CURATED_VERIFIED), not its scoring status.
  const support = sup ? { id: sup.id, label: sup.label, score: sup.score, contribution: sup.contribution_pp, tier: sup.evidence_class || sup.status } : null;

  const rs = metrics?.risk_state_bridge ?? null;
  const drivers: any[] = Array.isArray(rs?.drivers) ? rs.drivers : [];
  const core = drivers.filter((d) => d.role === 'core' && d.level && d.level !== 'NOT_MEASURED');
  const elevated = core.filter((d) => /HIGH|ELEVATED|CRITICAL/i.test(String(d.level)));
  const riskState: string | null = rs?.state ?? metrics?.risk_state ?? null;
  const coverage = typeof metrics?.scoreable_nominal_coverage === 'number' ? metrics.scoreable_nominal_coverage : null;
  const eq = typeof metrics?.evidence_quality === 'number' ? metrics.evidence_quality : null;

  // SR-361 watch chain: adverse pillar → current-market risk → coverage gap → none.
  // A withheld composite has no pillar contributions, so no pillar is its watch (the chain moves to risk).
  const adverse = compositeWithheld ? [] : pillars.filter((p) => p.id !== support?.id && (p.contribution_pp < 0 || p.score < 60));
  let watch: ExecutiveState['watch'];
  if (adverse.length) {
    const w = adverse.reduce((a, b) => (b.contribution_pp < a.contribution_pp ? b : a));
    watch = { kind: 'pillar', id: w.id, title: w.label, value: w.score.toFixed(1), unit: '/100' };
  } else if (riskState && ADVERSE_RISK.test(riskState)) {
    const vol = drivers.find((d) => d.id === 'vol_30d' && typeof d.value === 'number');
    watch = vol
      ? { kind: 'risk', id: 'vol_30d', title: '30D volatility', value: `${vol.value.toFixed(1)}%`, unit: `ann. · risk state ${String(riskState).toUpperCase()}` }
      : { kind: 'risk', title: 'Current-market risk', value: String(riskState).toUpperCase(), unit: 'risk state' };
  } else if (coverage != null && coverage < 0.999) {
    watch = { kind: 'coverage', title: 'Evidence coverage', value: `${Math.round(coverage * 100)}%`, unit: 'scoreable' };
  } else if (compositeWithheld) {
    watch = { kind: 'none', title: WITHHELD_PILLAR_STRENGTH, value: '—', unit: '' };
  } else if (pillars.some((p) => p.id !== 'esg' && p.score < 50)) {
    // "No weakness" is never stated while any pillar sits below the neutral 50.
    const low = pillars.filter((p) => p.id !== 'esg').reduce((a, b) => (b.score < a.score ? b : a));
    watch = { kind: 'pillar', id: low.id, title: low.label, value: low.score.toFixed(1), unit: '/100' };
  } else {
    watch = { kind: 'none', title: 'No quality-pillar weakness identified', value: '—', unit: '' };
  }

  const runTier: string | null = metrics?.data_quality_tier ?? null;
  const evidenceState: ExecutiveState['evidenceState'] =
    coverage != null && coverage >= 0.999 && runTier === 'LIVE_VERIFIED' ? 'COMPLETE' : coverage != null && coverage < 0.75 ? 'LIMITED' : 'PARTIAL';
  return {
    support, watch, riskState, elevatedCoreDrivers: elevated.length, coreDrivers: core.length, coverage, evidenceQuality: eq,
    runTier, evidenceState, tail: metrics?.var_backtest_traffic_light ?? null, compositeWithheld,
  };
}
